import { captureException, captureMessage } from '@sentry/react';
import { ChatCompletionCreateParamsNonStreaming } from 'openai/resources/chat';
import { useCallback, useState } from 'react';
import { getOpenAILLM } from '../utils';

interface ChatResponse {
  content: string;
}

export const useOpenAIChatCompletions = () => {
  const [chatResponse, setChatResponse] = useState<ChatResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchChatResponse = useCallback(
    async (params: ChatCompletionCreateParamsNonStreaming) => {
      setIsLoading(true);
      setError(null);

      try {
        const openai = getOpenAILLM();
        const response = await openai.chat.completions.create(params);

        if (!response?.choices || response?.choices?.length === 0) {
          captureMessage('openai responded with empty response', {
            extra: {
              response: JSON.stringify(response),
              params: JSON.stringify(params),
            },
            level: 'info',
          });
        }

        const content = response.choices[0]?.message?.content;
        if (content) {
          setChatResponse({ content });
        } else {
          throw new Error('No content in the response');
        }
      } catch (err) {
        if (err instanceof Error) {
          setError(err);
        } else {
          setError(new Error(`error while calling OpenAI ${err}`));
        }
        captureException(new Error('error while calling OpenAI'), {
          extra: {
            err,
            params: JSON.stringify(params),
          },
        });
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return [fetchChatResponse, { chatResponse, isLoading, error }] as const;
};
